import React, { useEffect, useState } from "react";
import bookingService from "../../services/bookingService";

interface RateDriverModalProps {
  open: boolean;
  bookingId: string;
  driverId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

const RateDriverModal: React.FC<RateDriverModalProps> = ({
  open,
  bookingId,
  driverId,
  onClose,
  onSubmitted,
}) => {
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRating(0);
      setHoverRating(0);
      setSelectedTags([]);
      setComment("");
      setError(null);
      return;
    }

    bookingService
      .getAvailableDriverTags()
      .then((res) => {
        if (res.success && res.data) setAvailableTags(res.data);
      })
      .catch(() => {
        // Non-fatal - rating can still be submitted without tags.
      });
  }, [open]);

  if (!open) return null;

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      setError("Please select a star rating.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await bookingService.submitDriverFeedback({
        booking_id: bookingId,
        driver_id: driverId,
        overall_rating: rating,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        comment: comment.trim() || undefined,
      });
      if (res.success) {
        onSubmitted();
        onClose();
      } else {
        setError(res.message || "Failed to submit rating.");
      }
    } catch (err: any) {
      setError(err.message || "Failed to submit rating.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-xl font-bold text-gray-800">Rate Your Driver</h2>
        <p className="mt-1 text-sm text-gray-500">
          How was your experience with the driver on this trip?
        </p>

        <div className="mt-5 flex justify-center gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="text-4xl leading-none focus:outline-none"
              aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
            >
              <span
                className={
                  star <= (hoverRating || rating)
                    ? "text-yellow-400"
                    : "text-gray-300"
                }
              >
                ★
              </span>
            </button>
          ))}
        </div>

        {availableTags.length > 0 && (
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {availableTags.map((tag) => {
              const selected = selectedTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    selected
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        )}

        <textarea
          className="mt-5 w-full rounded-md border border-gray-300 p-3 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
          rows={3}
          placeholder="Add a comment (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || rating < 1}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Submitting..." : "Submit Rating"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RateDriverModal;
